import HiddenInput from "@/components/form/components/HiddenInput";
import AntIcon from "@/components/icons/AntIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import { List } from "@/components/list/List";
import { paginationStandardProps, selectStandardProps } from "@/utils/componentUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { numberOrZero, replaceInArray } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { App as AntApp, Button, Checkbox, Col, Form, Input, Popconfirm, Row, Select } from "antd";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateVehicleModelMappings } from "../../../../../common/api/queries";
import { rowGutter } from "../../../../../common/constants";
import { RootArrayFormObject } from "../../../../../common/types";
import type { UUID } from "../../../../../typings/global";
import { VehicleMappingType } from "../../enums";
import {
  UpdateVehicleMapping,
  UpdateVehicleMappingFormItem,
  VehicleMapping,
  VehicleMappingData,
  VehicleModelMappingFilterPageResult,
} from "../../types";
import {
  parseVehicleMappingsFromIdentifiers,
  parseVehicleMappingsToUpdateFormItems,
  VEHICLE_IDENTIFIER_DELIMITER,
  VEHICLE_TYPES_PATH,
} from "../../utils";

interface Props {
  selectedBrandId: UUID;
  selectedMappingTypes: VehicleMappingType[];
  mappingData: VehicleMappingData[];
  currentPage?: VehicleModelMappingFilterPageResult;
  updatedRowsIds: UUID[];
  setUpdatedRowsIds: (updatedRowsIds: UUID[]) => void;
  onSearchSubmit: (keyword: string) => void;
  onExcludeMappedChange: (excludeMapped: boolean) => void;
  onListPageChange: (pageNumber: number) => void;
}

const VehicleModelMappingsListForm = ({
  selectedMappingTypes,
  mappingData,
  currentPage,
  selectedBrandId,
  updatedRowsIds,
  setUpdatedRowsIds,
  onSearchSubmit,
  onExcludeMappedChange,
  onListPageChange,
}: Props) => {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();

  const [form] = Form.useForm<RootArrayFormObject<UpdateVehicleMappingFormItem>>();
  const updateVehicleModelMappings = useUpdateVehicleModelMappings();

  useEffect(() => {
    if (currentPage?.pageData) {
      form.setFieldsValue({
        items: parseVehicleMappingsToUpdateFormItems(...currentPage.pageData),
      });
    }

    setUpdatedRowsIds([]);
  }, [currentPage, form]);

  const handleFormFinish = (values: RootArrayFormObject<UpdateVehicleMappingFormItem>) => {
    if (updatedRowsIds.length > 0) {
      updateVehicleModelMappings.mutate(
        {
          id: selectedBrandId,
          object: {
            mappings: values.items
              .filter((mapping) => updatedRowsIds.includes(mapping.id))
              .map<UpdateVehicleMapping>(
                (mapping) => parseVehicleMappingsFromIdentifiers(mapping) as UpdateVehicleMapping,
              ),
          },
        },
        { onError: (error) => setErrorsToForm(form, "vehicle.attrs", error.violations) },
      );
    } else {
      message.warning(t("vehicle.validations.noChanges"));
    }
  };

  const handleMappingRowChange = (id: UUID) => {
    if (!updatedRowsIds.includes(id)) {
      setUpdatedRowsIds([...updatedRowsIds, id]);
    }
  };

  const handleRowResetClick = (rowId: UUID) => {
    const row = currentPage?.pageData.find((row) => row.id === rowId);
    if (!row) {
      return;
    }
    const allRows = form.getFieldsValue().items;
    const newValue = parseVehicleMappingsToUpdateFormItems(row)[0];
    if (newValue) {
      form.setFieldsValue({
        items: replaceInArray(
          allRows,
          (item) => item?.id === rowId,
          () => newValue,
        ),
      });
    }

    setUpdatedRowsIds(updatedRowsIds.filter((stateRowId) => stateRowId !== rowId));
  };

  return (
    <>
      <Row gutter={rowGutter}>
        <Col span={16}>
          <Input.Search
            style={{ width: 350 }}
            enterButton
            allowClear
            defaultValue={currentPage?.keyword}
            placeholder={t("vehicle.helpers.searchModelHint")}
            onSearch={onSearchSubmit}
          />

          <Checkbox
            className="margin-left-small"
            style={{ marginTop: 4 }}
            checked={currentPage?.excludeMapped}
            onChange={(e) => onExcludeMappedChange(e.target.checked)}
          >
            {t("vehicle.actions.excludeMapped")}
          </Checkbox>
        </Col>
      </Row>

      <Form form={form} layout="vertical" onFinish={handleFormFinish} name="vehicleModelMappingsListForm">
        <List<VehicleMapping>
          items={currentPage?.pageData ?? []}
          gap={12}
          split
          pagination={{
            ...paginationStandardProps,
            mode: "server",
            current: numberOrZero(currentPage?.pageIndex) + 1,
            pageSize: currentPage?.pageSize,
            total: numberOrZero(currentPage?.totalElementsCount),
            onChange: onListPageChange,
          }}
          renderItem={(item, rowIndex) => (
            <div>
              <HiddenInput name={["items", rowIndex, "id"]} />
              <HiddenInput name={["items", rowIndex, "optimisticLockVersion"]} />

              <Row
                gutter={rowGutter}
                className={"vehicle-mappings-row" + (updatedRowsIds.includes(item.id) ? " edited-row" : "")}
              >
                <Col span={4}>
                  <Form.Item
                    name={["items", rowIndex, "name"]}
                    label={t("vehicle.attrs.name")}
                    rules={[validations.notBlank]}
                  >
                    <Input size="small" onChange={() => handleMappingRowChange(item.id)} />
                  </Form.Item>
                </Col>

                <Col span={16}>
                  <Row gutter={rowGutter}>
                    {Object.values(VehicleMappingType).map((type) => {
                      const typePath = VEHICLE_TYPES_PATH[type];
                      const mapping = mappingData.find((mapping) => mapping.type === type);
                      const brandMapping = mapping
                        ? mapping.brands.find(
                            (brand) => currentPage?.brand && currentPage.brand[typePath.keyId] === brand.id,
                          )
                        : undefined;

                      return (
                        <React.Fragment key={type}>
                          <HiddenInput name={["items", rowIndex, typePath.keyId]} />
                          <HiddenInput name={["items", rowIndex, typePath.keyName]} />

                          {selectedMappingTypes.includes(type) ? (
                            <Col span={6} key={type}>
                              <Form.Item
                                name={["items", rowIndex, typePath.keyIdentifier]}
                                label={t(`vehicle.enums.vehicleMappingType.${type}`)}
                                rules={[validations.none]}
                              >
                                <Select
                                  {...selectStandardProps}
                                  allowClear
                                  size="small"
                                  options={(brandMapping && brandMapping.models ? brandMapping.models : []).map(
                                    (item) => ({
                                      value: item.id + VEHICLE_IDENTIFIER_DELIMITER + item.name,
                                      label: `${item.name} (${item.id})`,
                                    }),
                                  )}
                                  onChange={() => handleMappingRowChange(item.id)}
                                />
                              </Form.Item>
                            </Col>
                          ) : (
                            <HiddenInput name={["items", rowIndex, typePath.keyIdentifier]} />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </Row>
                </Col>

                <Col span={4} className="right-align">
                  <Button
                    size="small"
                    icon={<AntIcon type="reload" />}
                    className="margin-bottom-tiny"
                    style={{ width: 100 }}
                    onClick={() => handleRowResetClick(item.id)}
                  >
                    {t("common.reset")}
                  </Button>
                  <br />

                  <Popconfirm
                    title={t("vehicle.titles.modelDelete")}
                    icon={<PopconfirmDeleteIcon />}
                    okText={t("common.yes")}
                    cancelText={t("common.no")}
                    okType="danger"
                    disabled
                  >
                    <Button
                      size="small"
                      icon={<AntIcon type="delete" />}
                      disabled
                      danger
                      className="margin-bottom-tiny"
                      style={{ width: 100 }}
                    >
                      {t("common.delete")}
                    </Button>
                  </Popconfirm>
                </Col>
              </Row>
            </div>
          )}
        />

        <Button className="margin-top-medium" type="primary" htmlType="submit" icon={<AntIcon type="save" />}>
          {t("common.save")}
        </Button>
      </Form>
    </>
  );
};

export default VehicleModelMappingsListForm;
